import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Label, LabelInput, Mailbox, MessageBody, ThreadDetail, ThreadPage } from "@dfws/contracts";
import { api, ApiFailure, setCsrf } from "./api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, err) => !(err instanceof ApiFailure && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

export interface SessionInfo {
  user: { id: number; email: string; name?: string | null; admin?: boolean };
  csrfToken: string;
}

export function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        const s = await api<SessionInfo>("/auth/session");
        setCsrf(s.csrfToken);
        return s;
      } catch (err) {
        if (err instanceof ApiFailure && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) => api<SessionInfo>("/auth/login", { method: "POST", body: input }),
    onSuccess: (s) => {
      setCsrf(s.csrfToken);
      qc.setQueryData(["session"], s);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/auth/logout", { method: "POST" }),
    onSettled: () => {
      qc.clear();
      qc.setQueryData(["session"], null);
    },
  });
}

export function useMailboxes(enabled = true) {
  return useQuery({
    queryKey: ["mailboxes"],
    queryFn: () => api<{ items: Mailbox[]; syncing: boolean }>("/mailboxes"),
    enabled,
    // While the first sync runs the folder list may be empty; poll until it lands (the WebSocket usually wins).
    refetchInterval: (q) => (q.state.data?.items.length ? false : 2000),
  });
}

const PAGE = 200;

/** A folder's conversations, or a label's conversations across folders. */
export function useThreads(scope: { mailboxId?: number; labelId?: number }) {
  const { mailboxId, labelId } = scope;
  return useInfiniteQuery({
    queryKey: labelId !== undefined ? ["threads", "label", labelId] : ["threads", mailboxId],
    queryFn: ({ pageParam }) => api<ThreadPage>(`/threads?${labelId !== undefined ? `labelId=${labelId}` : `mailboxId=${mailboxId}`}&limit=${PAGE}&cursor=${pageParam}`),
    initialPageParam: "0",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: mailboxId !== undefined || labelId !== undefined,
  });
}

/** Search results, Gmail operators included. The server also searches message bodies (full-text index). */
export function useSearchThreads(q: string) {
  return useInfiniteQuery({
    queryKey: ["threads", "search", q],
    queryFn: ({ pageParam }) => api<ThreadPage & { partial: boolean }>(`/search?q=${encodeURIComponent(q)}&limit=100&cursor=${pageParam}`),
    initialPageParam: "0",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: q.trim().length > 0,
    staleTime: 15_000,
  });
}

// ----- Snooze -----

export function useSnooze() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { threadKeys: string[]; until: Date | null }) =>
      Promise.all(v.threadKeys.map((k) => api(`/threads/${encodeURIComponent(k)}/snooze`, v.until ? { method: "PUT", body: { until: v.until.toISOString() } } : { method: "DELETE" }))),
    onMutate: (v) => {
      // Gone from the inbox at once; the Snoozed folder fills in when the sync lands.
      const keys = new Set(v.threadKeys);
      if (v.until) qc.setQueriesData<{ pages: ThreadPage[]; pageParams: string[] }>({ queryKey: ["threads"] }, (data) =>
        data ? { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.filter((t) => !keys.has(t.key)) })) } : data,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["mailboxes"] });
      setTimeout(() => void qc.invalidateQueries({ queryKey: ["threads"] }), 1500);
    },
  });
}

// ----- Labels -----

export function useLabels() {
  return useQuery({ queryKey: ["labels"], queryFn: () => api<{ items: Label[] }>("/labels"), staleTime: 60_000 });
}

export function useSaveLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: number; input: LabelInput }) =>
      v.id ? api(`/labels/${v.id}`, { method: "PUT", body: v.input }).then(() => null) : api<Label>("/labels", { method: "POST", body: v.input }),
    onMutate: (v) => {
      if (v.id) qc.setQueryData<{ items: Label[] }>(["labels"], (d) => (d ? { items: d.items.map((l) => (l.id === v.id ? { ...l, ...v.input } : l)) } : d));
    },
    onSuccess: (created) => {
      if (created) qc.setQueryData<{ items: Label[] }>(["labels"], (d) => (d ? { items: [...d.items, created] } : { items: [created] }));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["labels"] }),
  });
}

export function useDeleteLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<{ ok: boolean; warning: string | null }>(`/labels/${id}`, { method: "DELETE" }),
    onMutate: (id) => {
      qc.setQueryData<{ items: Label[] }>(["labels"], (d) => (d ? { items: d.items.filter((l) => l.id !== id) } : d));
      // Chips disappear from every open list and conversation at once.
      qc.setQueriesData<{ pages: ThreadPage[]; pageParams: string[] }>({ queryKey: ["threads"] }, (data) =>
        data ? { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.map((t) => (t.labels?.includes(id) ? { ...t, labels: t.labels.filter((x) => x !== id) } : t)) })) } : data,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["labels"] });
      void qc.invalidateQueries({ queryKey: ["filters"] });
      void qc.invalidateQueries({ queryKey: ["thread"] });
    },
  });
}

export function useThread(key: string | null) {
  return useQuery({ ...threadQuery(key ?? ""), enabled: Boolean(key) });
}

const bodyQuery = (id: number, images: boolean) => ({
  queryKey: ["body", id, images],
  queryFn: () => api<MessageBody>(`/messages/${id}/body${images ? "?images=1" : ""}`),
  staleTime: 10 * 60_000,
});

export function useMessageBody(id: number, images: boolean, enabled: boolean) {
  return useQuery({ ...bodyQuery(id, images), enabled });
}

const threadQuery = (key: string) => ({ queryKey: ["thread", key], queryFn: () => api<ThreadDetail>(`/threads/${encodeURIComponent(key)}`), staleTime: 60_000 });

/**
 * Warms a conversation before it is opened (hover, keyboard cursor, next and previous of the open one),
 * so opening it needs no round trip. Fetches the conversation and the newest message's body.
 */
export function prefetchConversation(key: string, latestMessageId: number, images: boolean): Promise<unknown> {
  return Promise.all([queryClient.prefetchQuery(threadQuery(key)), queryClient.prefetchQuery(bodyQuery(latestMessageId, images))]);
}

let opSeq = 0;

/** Optimistic flag changes: the cache updates first, the server confirms over the WebSocket. */
export type MessageActionName = "read" | "unread" | "star" | "unstar" | "archive" | "trash" | "label" | "unlabel" | "move" | "inbox";
type ThreadData = { pages: ThreadPage[]; pageParams: string[] };

export function useMessageAction() {
  const qc = useQueryClient();
  return useMutation({
    /** threadKey for one conversation; threadKeys with wholeThread for a list selection (the server expands to every message). */
    mutationFn: (v: { messageIds: number[]; action: MessageActionName; threadKey?: string; threadKeys?: string[]; wholeThread?: boolean; labelId?: number; mailboxId?: number }) =>
      api("/messages/actions", { method: "POST", body: { opId: `op${++opSeq}`, messageIds: v.messageIds, action: v.action, labelId: v.labelId, mailboxId: v.mailboxId, wholeThread: v.wholeThread } }),
    onMutate: (v) => {
      const keys = new Set(v.threadKeys ?? (v.threadKey ? [v.threadKey] : []));
      if (!keys.size) return;
      const mapThreads = (fn: (t: ThreadPage["items"][number]) => ThreadPage["items"][number] | null) =>
        qc.setQueriesData<ThreadData>({ queryKey: ["threads"] }, (data) =>
          data ? { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.flatMap((t) => (keys.has(t.key) ? (fn(t) ?? []) : [t])) })) } : data,
        );
      if (v.action === "label" || v.action === "unlabel") {
        const id = v.labelId!;
        const apply = (ids: number[] | undefined) => (v.action === "label" ? [...new Set([...(ids ?? []), id])] : (ids ?? []).filter((x) => x !== id));
        mapThreads((t) => ({ ...t, labels: apply(t.labels) }));
        for (const k of keys) qc.setQueryData<ThreadDetail>(["thread", k], (d) => (d ? { ...d, labels: apply(d.labels) } : d));
        return;
      }
      if (v.action === "archive" || v.action === "trash" || v.action === "move" || v.action === "inbox") {
        // Gone from every list and from the open conversation at once.
        mapThreads((t) => {
          const detail = qc.getQueryData<ThreadDetail>(["thread", t.key]);
          const whole = v.wholeThread || !detail || detail.messages.every((m) => v.messageIds.includes(m.id));
          return whole ? null : t;
        });
        for (const k of keys) qc.setQueryData<ThreadDetail>(["thread", k], (d) => (d ? { ...d, messages: v.wholeThread ? [] : d.messages.filter((m) => !v.messageIds.includes(m.id)) } : d));
        return;
      }
      const unread = v.action === "unread" ? true : v.action === "read" ? false : undefined;
      const starred = v.action === "star" ? true : v.action === "unstar" ? false : undefined;
      const patch = { ...(unread !== undefined && { unread }), ...(starred !== undefined && { starred }) };
      mapThreads((t) => ({ ...t, ...patch }));
      for (const k of keys)
        qc.setQueryData<ThreadDetail>(["thread", k], (d) => (d ? { ...d, messages: d.messages.map((m) => (v.wholeThread || v.messageIds.includes(m.id) ? { ...m, ...patch } : m)) } : d));
    },
    onSettled: (_d, _e, v) => {
      void qc.invalidateQueries({ queryKey: ["mailboxes"] });
      if (v.action === "label" || v.action === "unlabel") {
        void qc.invalidateQueries({ queryKey: ["labels"] });
        // The label's own view gains or loses the conversation.
        void qc.invalidateQueries({ queryKey: ["threads", "label", v.labelId] });
      }
      if (v.action === "archive" || v.action === "trash" || v.action === "move" || v.action === "inbox") setTimeout(() => void qc.invalidateQueries({ queryKey: ["threads"] }), 1500);
    },
  });
}

// ----- Settings, filters, out of office, sessions -----

export interface FilterCriteria { from?: string; to?: string; subject?: string; hasWords?: string; doesNotHave?: string; hasAttachment?: boolean; sizeOverMb?: number; sizeUnderMb?: number; kind?: "newsletter" | "receipt" | "notification" | "screener" }
export interface FilterActions { markRead?: boolean; star?: boolean; skipInbox?: boolean; moveTo?: string; forwardTo?: string; delete?: boolean; spam?: boolean; labelIds?: number[] }
export interface Filter { id: number; enabled: boolean; criteria: FilterCriteria; actions: FilterActions }
export interface Vacation { enabled: boolean; startDate: string | null; endDate: string | null; subject: string; body: string; onlyDomain: boolean }

export function useFilters() {
  return useQuery({ queryKey: ["filters"], queryFn: () => api<{ items: Filter[]; syncError: string | null }>("/filters") });
}

export function useSaveFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (f: Omit<Filter, "id"> & { id?: number }) =>
      f.id ? api<{ id?: number; warning: string | null }>(`/filters/${f.id}`, { method: "PUT", body: f }) : api<{ id?: number; warning: string | null }>("/filters", { method: "POST", body: f }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["filters"] }),
  });
}

export function useDeleteFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<{ warning: string | null }>(`/filters/${id}`, { method: "DELETE" }),
    onMutate: (id) => qc.setQueryData<{ items: Filter[]; syncError: string | null }>(["filters"], (d) => (d ? { ...d, items: d.items.filter((f) => f.id !== id) } : d)),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["filters"] }),
  });
}

export function useFilterPreview(criteria: FilterCriteria, enabled: boolean) {
  return useQuery({
    queryKey: ["filter-preview", criteria],
    queryFn: () => api<{ total: number; approximate: boolean; samples: { subject: string; from: string | null; date: string }[] }>("/filters/preview", { method: "POST", body: criteria }),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useVacation() {
  return useQuery({ queryKey: ["vacation"], queryFn: () => api<Vacation>("/vacation") });
}

export function useSaveVacation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: Vacation) => api<{ warning: string | null }>("/vacation", { method: "PUT", body: v }),
    onSuccess: (_r, v) => qc.setQueryData(["vacation"], v),
  });
}

export function useSessions() {
  return useQuery({ queryKey: ["sessions"], queryFn: () => api<{ items: { id: string; current: boolean; createdAt: string; ip: string; userAgent: string }[] }>("/auth/sessions") });
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api<{ revoked: number }>("/auth/sessions/revoke-others", { method: "POST" }), onSettled: () => void qc.invalidateQueries({ queryKey: ["sessions"] }) });
}
